// ─── The journey, as a flat plan — one row per step ───────────────────────────
// The tree (components/JourneyTree.jsx) shows the SHAPE; this shows the PLAN:
// every branch, when each message actually goes and what it says — the form
// people review, sign off and paste into a deck. It is DERIVED from the stored
// tree every time, never saved next to it: edit a delay or a subject and the
// summary follows, where a stored copy of the Owl's chat table would quietly go
// stale the first time someone touched a step.
//
// The timing is the part worth being careful about. A behaviour decision waits UP
// TO `waitHours`, and a reactive branch (bought / clicked / opened / in_segment)
// fires the moment the person acts — only the `timeout` branch, the silence,
// actually serves the full window. So the wait rolls into timeout branches, while
// a reactive branch's first step carries a `trigger` and reads "as soon as they
// click", which is how a human describes it. Splits fork instantly and add
// nothing. A HELD step (or one inside a held branch) is marked `paused`, because a
// plan that lists a step which currently sends to nobody is not the plan.
// Pure, no React — imported by the component and by test/journeyFlow.

const TRIGGER_TEXT = {
  bought: 'as soon as they buy',
  clicked: 'as soon as they click',
  opened: 'as soon as they open',
  in_segment: 'when they join that list',
};

const platLabel = (p) => (p === 'both' ? 'Meta + TikTok' : p === 'tiktok' ? 'TikTok' : 'Meta');

// `after` is a STEP-TO-STEP delta — "how long after the step before me" — which is
// what the table column says. It is NOT the offset from the start, and reading it
// as one is a real bug this module has already shipped once: the tree's date chip
// did `start + after`, so every step past the first in a branch showed a date too
// early by the whole delay chain above it (a 2nd email read 16:01 when the engine
// would send it at 17:01, and on a journey with a 3-day opener it would have been
// three days out). So the running clock is kept here, once, as `at` — an absolute
// instant — and callers that want a date use that and never re-derive one.
//
// `startMs` is when the journey begins (its scheduled start, or the moment it went
// live). Default 0 makes `at` a plain hours-from-start count in ms, which is what
// journeySpanHours measures. A step PINNED to a date resets the clock to the pin —
// everything after it is measured from there, exactly as the engine schedules it
// (server/journeys.js dueAt: sendAt wins over delayHours).
//
// → [{ id, branch, kind: 'Email'|'SMS'|'Audience'|'Handoff', after (hours), at (ms), pinned, trigger, subject, sync, handoff }]
export function journeyFlow(nodes, startMs = 0) {
  const rows = [];
  const walk = (ns, branch, carry, trigger, held, clock, pinnedAbove) => {
    let pending = carry || 0;
    let fires = trigger || '';
    let at = clock;
    let pinned = pinnedAbove;
    for (const n of ns || []) {
      if (n.type === 'message') {
        // A step pinned to a DATE doesn't accumulate the running delay — it
        // goes when it goes, so `sendAt` is carried and the hour count reset.
        const t = n.sendAt ? Date.parse(n.sendAt) : NaN;
        if (Number.isFinite(t)) { at = t; pinned = true; } else at += ((n.delayHours || 0) + pending) * 3600e3;
        rows.push({
          id: n.id, branch, kind: n.channel === 'sms' ? 'SMS' : 'Email',
          after: n.sendAt ? 0 : (n.delayHours || 0) + pending, sendAt: n.sendAt || '',
          at, pinned, trigger: fires, paused: !!(held || n.paused),
          subject: n.channel === 'sms' ? String(n.body || '').slice(0, 90) : (n.subject || '(no subject yet)'),
        });
        pending = 0; fires = '';
      } else if (n.type === 'sync') {
        at += pending * 3600e3;
        rows.push({
          id: n.id, branch, kind: 'Audience', after: pending, at, pinned, trigger: fires, sync: true, paused: !!(held || n.paused),
          subject: `${n.action === 'remove' ? 'Remove from' : 'Add to'} ${platLabel(n.platform)}${n.audienceName ? ` — ${n.audienceName}` : ''}`,
        });
        pending = 0; fires = '';
      } else if (n.type === 'handoff') {
        // ➡ A pass-through like the audience sync: it takes no time of its own,
        // so it inherits the running delay and never adds to the journey's span.
        at += pending * 3600e3;
        rows.push({
          id: n.id, branch, kind: 'Handoff', after: pending, at, pinned, trigger: fires, handoff: true, paused: !!(held || n.paused),
          subject: n.target === 'segment'
            ? `Add to the list${n.segmentName ? ` — ${n.segmentName}` : ' — (no list named)'}`
            : `Send to another journey${n.targetActionId ? '' : ' — (none chosen)'}`,
        });
        pending = 0; fires = '';
      } else if (n.type === 'decision') {
        for (const b of n.branches || []) {
          const waits = n.kind !== 'split' && b.when === 'timeout';
          walk(
            b.nodes,
            [branch, b.label].filter(Boolean).join(' → '),
            pending + (waits ? (n.waitHours || 0) : 0),
            n.kind === 'split' || waits ? fires : (TRIGGER_TEXT[b.when] || fires),
            held || b.paused, // a held branch holds everything inside it
            at, pinned, // the clock carries INTO every branch — that is the fix above
          );
        }
        return; // everything after a decision lives inside its branches
      }
    }
  };
  walk(nodes, '', 0, '', false, startMs, false);
  return rows;
}

// Longest path through the journey, in hours — "how long is someone in this?".
// Reactive branches are best-case-instant, so the window is driven by the waits.
// Measured off the running clock, so a delay on the opening message counts too;
// a path with a PINNED date has no relative span and is left out of the max.
export function journeySpanHours(nodes) {
  const ends = journeyFlow(nodes).filter((r) => !r.pinned).map((r) => r.at / 3600e3);
  return Math.max(0, ...ends);
}
